import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { Component } from 'react';
import LoadingOverlay from 'react-loading-overlay';

import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import {
    endpoint,
    PERMISSION_DEFAULTS,
    PERMISSION_DISPLAY_ORDER,
    PERMISSIONS,
    ROLE,
    ROLE_NAMES,
    snackbarVariant,
    tableTranslations,
} from '../../utils/constants';
import {
    errorHandling,
    hasModuleAccess,
    isAdminRole,
    permissionsFromBitmask,
    permissionsToBitmask,
} from '../../utils/helpers';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';

const permissionChipStyles = {
    root: {
        margin: '1px',
        height: 20,
        fontSize: '.7rem',
    },
};

const LOGIN_PATTERN = /^[a-z][a-z0-9_]{3,}$/;
const LOGIN_DISALLOWED_CHARACTERS = /[^a-z0-9_]/g;
const LOGIN_START_CHARACTER = /^[a-z]/;

const validateLogin = login => {
    if (!login) {
        return 'Login jest wymagany';
    }

    if (login.length < 4) {
        return 'Login musi mieć co najmniej 4 znaki';
    }

    if (!LOGIN_START_CHARACTER.test(login)) {
        return 'Login musi zaczynać się od litery';
    }

    if (!LOGIN_PATTERN.test(login)) {
        return 'Login może zawierać tylko małe angielskie litery, cyfry i znak podkreślenia';
    }

    return null;
};

const PermissionChip = withStyles(permissionChipStyles)(Chip);

const PermissionsCell = ({ value, data }) => {
    if (Number(data?.role) === ROLE.ADMIN) {
        return <span>-</span>;
    }

    const permissions = permissionsFromBitmask(value);

    if (!permissions.length) {
        return <span>Brak uprawnień</span>;
    }

    return permissions.map(permission => (
        <PermissionChip
            key={permission}
            label={PERMISSIONS[permission] ?? permission}
            size="small"
        />
    ));
};

const UserActionsCell = ({ data, context }) => {
    const isDeleteDisabled =
        Number(data?.role) === ROLE.TECHNICIAN ||
        data?.id === context.currentUserId;

    return (
        <>
            <IconButton size="small" title="Edytuj" onClick={() => context.onEdit(data)}>
                <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
                size="small"
                title={isDeleteDisabled ? 'Tego użytkownika nie można usunąć' : 'Usuń'}
                disabled={isDeleteDisabled}
                onClick={() => context.onDelete(data)}>
                <DeleteIcon fontSize="small" />
            </IconButton>
        </>
    );
};

const tableStyle = {
    fontFamily: 'inherit',
    height: '65vh',
};

const styles = () => ({
    root: {
        margin: '1rem 2rem',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        marginBottom: '1rem',
    },
    addButton: {
        textTransform: 'none',
    },
    dialogPaper: {
        minWidth: 420,
    },
    formField: {
        marginTop: '1rem',
        width: '100%',
    },
    roleHint: {
        fontSize: '.78rem',
        color: '#777',
        marginTop: '.25rem',
    },
    permissionsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        marginTop: '.5rem',
    },
    permissionLabel: {
        fontSize: '.78rem',
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '50vh',
    },
});

class UsersList extends Component {
    state = {
        loading: false,
        isServerError: false,
        users: [],
        currentUserId: null,
        isFormOpen: false,
        editingUser: null,
        form: {
            name: '',
            password: '',
            password_confirmation: '',
            role: ROLE.USER,
            permissions: [...PERMISSION_DEFAULTS],
        },
        isDeleteDialogOpen: false,
        userToDelete: null,
        isSubmitting: false,
        snackbarData: {
            isSnackbarOpen: false,
            message: '',
            errorInfo: '',
            variant: '',
        },
    };

    componentDidMount() {
        this.cancelHttpReq = cancelToken.source();
        this.fetchUsers();
        this.fetchCurrentUserId();
    }

    componentWillUnmount() {
        this.cancelHttpReq.cancel();
    }

    fetchUsers = () => {
        this.setState({ loading: true });

        axiosInstance
            .get(endpoint.USER_SHOW, {
                cancelToken: this.cancelHttpReq.token,
            })
            .then(({ data }) => {
                this.setState({
                    loading: false,
                    users: data.data,
                });
            })
            .catch(err => {
                errorHandling(err, this);
                this.setState({ loading: false });
            });
    };

    fetchCurrentUserId = () => {
        axiosInstance
            .get(endpoint.USER_CURRENT)
            .then(({ data }) => {
                this.setState({ currentUserId: data.id ?? null });
            })
            .catch(() => {
                // Deleting the logged-in account is also blocked server-side, so
                // this is only used to disable the action in the UI.
            });
    };

    openCreateDialog = () => {
        this.setState({
            isFormOpen: true,
            editingUser: null,
            form: {
                name: '',
                password: '',
                password_confirmation: '',
                role: ROLE.USER,
                permissions: [...PERMISSION_DEFAULTS],
            },
        });
    };

    openEditDialog = user => {
        this.setState({
            isFormOpen: true,
            editingUser: user,
            form: {
                name: user.name || '',
                password: '',
                password_confirmation: '',
                role: Number(user.role),
                permissions: permissionsFromBitmask(user.permissions),
            },
        });
    };

    closeFormDialog = () => {
        this.setState({ isFormOpen: false, editingUser: null });
    };

    handleFormChange = event => {
        const { name, value } = event.target;
        this.setState(prevState => ({
            form: {
                ...prevState.form,
                [name]: value,
            },
        }));
    };

    handleLoginChange = event => {
        const login = event.target.value
            .toLowerCase()
            .replace(LOGIN_DISALLOWED_CHARACTERS, '');

        this.setState(prevState => ({
            form: {
                ...prevState.form,
                name: login,
            },
        }));
    };

    handleRoleChange = event => {
        const role = Number(event.target.value);
        const isTechnicianValue = role === ROLE.TECHNICIAN;

        this.setState(prevState => ({
            form: {
                ...prevState.form,
                role,
                password: isTechnicianValue ? '' : prevState.form.password,
                password_confirmation: isTechnicianValue
                    ? ''
                    : prevState.form.password_confirmation,
                permissions: [...(prevState.form.permissions ?? PERMISSION_DEFAULTS)],
            },
        }));
    };

    handlePermissionToggle = permission => {
        this.setState(prevState => {
            const isChecked = prevState.form.permissions.includes(permission);
            const permissions = isChecked
                ? prevState.form.permissions.filter(item => item !== permission)
                : [...prevState.form.permissions, permission];

            return {
                form: {
                    ...prevState.form,
                    permissions,
                },
            };
        });
    };

    validateForm = () => {
        const { form, editingUser } = this.state;
        const isPasswordRequired =
            !editingUser &&
            form.role !== ROLE.TECHNICIAN &&
            !form.password;

        const loginMessage = validateLogin(form.name);

        if (loginMessage) {
            return loginMessage;
        }

        if (isPasswordRequired) {
            return 'Hasło jest wymagane';
        }

        if (form.password && form.password.length < 8) {
            return 'Hasło musi mieć co najmniej 8 znaków';
        }

        if (form.password !== form.password_confirmation) {
            return 'Hasła nie są identyczne';
        }

        return null;
    };

    showSnackbar = (message, variant, errorInfo = '') => {
        this.setState({
            snackbarData: {
                isSnackbarOpen: true,
                message,
                errorInfo,
                variant,
            },
        });
    };

    handleSnackbarClose = () => {
        this.setState({
            snackbarData: {
                isSnackbarOpen: false,
                message: '',
                errorInfo: '',
                variant: '',
            },
        });
    };

    submitUser = () => {
        const validationMessage = this.validateForm();

        if (validationMessage) {
            this.showSnackbar(validationMessage, snackbarVariant.ERROR);
            return;
        }

        const { form, editingUser } = this.state;
        const isAdminRoleValue = form.role === ROLE.ADMIN;
        const payload = {
            name: form.name.trim(),
            role: form.role,
        };

        if (!isAdminRoleValue) {
            payload.permissions = permissionsToBitmask(form.permissions);
        }

        if (form.password) {
            payload.password = form.password;
            payload.password_confirmation = form.password_confirmation;
        }

        if (editingUser) {
            payload.user_id = editingUser.id;
        }

        this.setState({ isSubmitting: true });

        const request = editingUser
            ? axiosInstance.post(endpoint.USER_UPDATE, payload)
            : axiosInstance.post(endpoint.USER_CREATE, payload);

        request
            .then(() => {
                this.setState({ isSubmitting: false });
                this.closeFormDialog();
                this.showSnackbar(
                    editingUser
                        ? 'Użytkownik został zaktualizowany'
                        : 'Użytkownik został dodany',
                    snackbarVariant.SUCCESS
                );
                this.fetchUsers();
            })
            .catch(err => {
                const firstError = this.getFirstValidationError(err);
                this.setState({ isSubmitting: false });
                this.showSnackbar(
                    'Nie udało się zapisać użytkownika',
                    snackbarVariant.ERROR,
                    firstError
                );
            });
    };

    getFirstValidationError = err => {
        const errorData = err?.response?.data?.error;

        if (!errorData) {
            return '';
        }

        if (typeof errorData === 'string') {
            return errorData;
        }

        const values = Object.values(errorData).flat();
        return values.length ? values[0] : '';
    };

    openDeleteDialog = user => {
        this.setState({ isDeleteDialogOpen: true, userToDelete: user });
    };

    closeDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: false, userToDelete: null });
    };

    deleteUser = () => {
        const { userToDelete } = this.state;

        if (!userToDelete) {
            return;
        }

        this.setState({ isSubmitting: true });

        axiosInstance
            .post(endpoint.USER_DELETE, {
                user_id: userToDelete.id,
            })
            .then(() => {
                this.setState({ isSubmitting: false });
                this.closeDeleteDialog();
                this.showSnackbar('Użytkownik został usunięty', snackbarVariant.SUCCESS);
                this.fetchUsers();
            })
            .catch(err => {
                this.setState({ isSubmitting: false });
                this.showSnackbar(
                    'Nie udało się usunąć użytkownika',
                    snackbarVariant.ERROR,
                    this.getFirstValidationError(err)
                );
            });
    };

    renderFormDialog = () => {
        const { classes } = this.props;
        const { isFormOpen, form, editingUser, isSubmitting, users } = this.state;
        const showPermissions = form.role !== ROLE.ADMIN;
        const isTechnicianValue = Number(form.role) === ROLE.TECHNICIAN;
        const hasTechnician = users.some(
            user => Number(user.role) === ROLE.TECHNICIAN
        );
        const isEditingTechnician =
            Boolean(editingUser) &&
            Number(editingUser.role) === ROLE.TECHNICIAN;
        const technicianOptionDisabled = !isEditingTechnician && hasTechnician;

        return (
            <Dialog
                open={isFormOpen}
                onClose={this.closeFormDialog}
                classes={{ paper: classes.dialogPaper }}
                maxWidth="sm"
                fullWidth>
                <DialogTitle>
                    {editingUser ? 'Edytuj użytkownika' : 'Dodaj użytkownika'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        className={classes.formField}
                        label="Login"
                        name="name"
                        value={form.name}
                        onChange={this.handleLoginChange}
                        helperText="Tylko małe angielskie litery, cyfry i znak podkreślenia (min. 4 znaki)"
                        variant="outlined"
                        required
                        autoFocus
                    />
                    <Select
                        className={classes.formField}
                        value={form.role}
                        onChange={this.handleRoleChange}
                        variant="outlined"
                        disabled={isEditingTechnician}>
                        {Object.values(ROLE).map(role => {
                            const isTechnicianRole = role === ROLE.TECHNICIAN;

                            return (
                                <MenuItem
                                    key={role}
                                    value={role}
                                    disabled={
                                        isTechnicianRole && technicianOptionDisabled
                                    }>
                                    {ROLE_NAMES[role]}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {isEditingTechnician && (
                        <div className={classes.roleHint}>
                            Konto technika jest stałe — nie można go usunąć ani
                            zmienić jego roli. Uprawnienia można edytować.
                        </div>
                    )}
                    {isTechnicianValue && !isEditingTechnician && hasTechnician && (
                        <div className={classes.roleHint}>
                            Konto technika już istnieje — można dodać tylko jednego
                            technika.
                        </div>
                    )}
                    {isTechnicianValue && (
                        <div className={classes.roleHint}>
                            Konto technika loguje się przez RKS — hasło nie jest
                            ustawiane.
                        </div>
                    )}
                    {!isTechnicianValue && (
                        <>
                            <TextField
                                className={classes.formField}
                                label={
                                    editingUser
                                        ? 'Nowe hasło (pozostaw puste, aby nie zmieniać)'
                                        : 'Hasło'
                                }
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                value={form.password}
                                onChange={this.handleFormChange}
                                variant="outlined"
                                required={!editingUser}
                            />
                            <TextField
                                className={classes.formField}
                                label="Potwierdź hasło"
                                name="password_confirmation"
                                type="password"
                                autoComplete="new-password"
                                value={form.password_confirmation}
                                onChange={this.handleFormChange}
                                variant="outlined"
                                required={Boolean(form.password)}
                            />
                        </>
                    )}
                    {showPermissions && (
                        <>
                            <div className={classes.formField}>Uprawnienia modułów</div>
                            <div className={classes.permissionsContainer}>
                                {PERMISSION_DISPLAY_ORDER.map(key => (
                                    <FormControlLabel
                                        key={key}
                                        control={
                                            <Checkbox
                                                checked={form.permissions.includes(key)}
                                                onChange={() => this.handlePermissionToggle(key)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={PERMISSIONS[key] ?? key}
                                        classes={{ label: classes.permissionLabel }}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.closeFormDialog} color="primary">
                        Anuluj
                    </Button>
                    <Button
                        onClick={this.submitUser}
                        color="primary"
                        variant="contained"
                        disabled={isSubmitting}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    renderDeleteDialog = () => {
        const { classes } = this.props;
        const { isDeleteDialogOpen, userToDelete, isSubmitting } = this.state;

        return (
            <Dialog
                open={isDeleteDialogOpen}
                onClose={this.closeDeleteDialog}
                classes={{ paper: classes.dialogPaper }}>
                <DialogTitle>
                    Czy na pewno chcesz usunąć użytkownika {userToDelete?.name}?
                </DialogTitle>
                <DialogActions>
                    <Button onClick={this.closeDeleteDialog} color="primary">
                        Nie
                    </Button>
                    <Button
                        onClick={this.deleteUser}
                        color="secondary"
                        variant="contained"
                        disabled={isSubmitting}>
                        Tak
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    renderUsersTable = () => {
        const { users, loading, currentUserId } = this.state;
        const isAdminMode = isAdminRole();

        if (loading) {
            return (
                <div className={this.props.classes.loadingWrapper}>
                    <LoadingOverlay active spinner text="Proszę czekać..." />
                </div>
            );
        }

        return (
            <div className={'ag-theme-alpine'} style={tableStyle}>
                <AgGridReact
                    localeText={tableTranslations}
                    rowData={users}
                    suppressCellSelection
                    pagination
                    paginationPageSize={50}
                    enableCellTextSelection
                    accentedSort
                    suppressNoRowsOverlay={!loading}
                    context={{
                        onEdit: this.openEditDialog,
                        onDelete: this.openDeleteDialog,
                        currentUserId,
                    }}>
                    <AgGridColumn
                        field="id"
                        headerName="ID"
                        width={70}
                        sortable
                        filter
                        resizable
                    />
                    <AgGridColumn
                        field="name"
                        headerName="LOGIN"
                        flex={1}
                        sortable
                        filter
                        resizable
                    />
                    <AgGridColumn
                        field="role"
                        headerName="ROLA"
                        width={150}
                        sortable
                        filter
                        resizable
                        valueFormatter={params => ROLE_NAMES[Number(params.value)] ?? params.value}
                    />
                    <AgGridColumn
                        field="permissions"
                        headerName="UPRAWNIENIA"
                        flex={2}
                        sortable={false}
                        filter={false}
                        resizable
                        cellRendererFramework={PermissionsCell}
                    />
                    {isAdminMode && (
                        <AgGridColumn
                            headerName="AKCJE"
                            width={110}
                            sortable={false}
                            filter={false}
                            cellRendererFramework={UserActionsCell}
                        />
                    )}
                </AgGridReact>
            </div>
        );
    };

    render() {
        const { classes } = this.props;
        const { isServerError, snackbarData, isSubmitting } = this.state;

        if (isServerError) {
            return null;
        }

        const isReadOnlyMode = !isAdminRole() && hasModuleAccess('users');

        if (!isAdminRole() && !isReadOnlyMode) {
            return null;
        }

        return (
            <div className={classes.root}>
                <TitleHeader title="Użytkownicy" />
                {isAdminRole() && (
                    <div className={classes.actions}>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.addButton}
                            onClick={this.openCreateDialog}>
                            Dodaj użytkownika
                        </Button>
                    </div>
                )}
                {this.renderUsersTable()}
                {this.renderFormDialog()}
                {this.renderDeleteDialog()}
                <Snackbar
                    snackbarData={snackbarData}
                    handleIsSnackbarOpen={this.handleSnackbarClose}
                />
                {isSubmitting && <LoadingOverlay active spinner text="Proszę czekać..." />}
            </div>
        );
    }
}

UsersList.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(UsersList);
